import axios from "axios";

const baseURL = process.env.REACT_APP_API_BASE_URL || "";

// client khusus ambil csrf dari backend
const csrfBootstrapClient = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
});

// axios utama
const api = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
});

const getCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;
const originalGet = api.get.bind(api);

function cacheKey(url, config) {
  const params = config?.params ? JSON.stringify(config.params) : "";
  return `${url}|${params}`;
}

function isCacheableGet(url, config) {
  if (config?.noCache) return false;
  const clean = String(url || "").split("?")[0];
  return clean === "/products" || clean === "/bom";
}

api.get = (url, config = {}) => {
  if (!isCacheableGet(url, config)) return originalGet(url, config);

  const key = cacheKey(url, config);
  const cached = getCache.get(key);
  
  if (cached?.promise) return cached.promise;
  
  if (cached?.response && cached.expiresAt > Date.now()) {
    return Promise.resolve({ ...cached.response, data: cached.response.data });
  }

  const promise = originalGet(url, config)
    .then((response) => {
      getCache.set(key, { response, expiresAt: Date.now() + CACHE_TTL_MS });
      return response;
    })
    .catch((err) => {
      getCache.delete(key);
      throw err;
    });

  getCache.set(key, { promise, expiresAt: Date.now() + CACHE_TTL_MS });
  return promise;
};

export function clearApiCache(prefix = "") {
  for (const key of getCache.keys()) {
    if (!prefix || key.startsWith(prefix)) getCache.delete(key);
  }
}

// simpan csrf di memory
let csrfMemoryToken = null;
let csrfPromise = null;

// cek method butuh csrf atau tidak
function needsCsrf(method) {
  const m = String(method || "GET").toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

// ambil csrf dari backend
async function ensureCsrfToken() {
  if (csrfMemoryToken) return csrfMemoryToken;

  if (!csrfPromise) {
    csrfPromise = csrfBootstrapClient
      .get("/auth/csrf")
      .then((res) => {
        csrfMemoryToken = res.data?.csrfToken;
        return csrfMemoryToken;
      })
      .finally(() => {
        csrfPromise = null;
      });
  }

  return csrfPromise;
}

// 🔥 INTERCEPTOR REQUEST (JWT + CSRF)
api.interceptors.request.use(async (config) => {
  // 🔐 JWT
  const jwtToken = localStorage.getItem("token");
  if (jwtToken) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${jwtToken}`;
  }

  // 🔐 CSRF
  if (needsCsrf(config.method)) {
    const token = await ensureCsrfToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers["x-csrf-token"] = token;
    }
  }

  return config;
});

// 🔥 HANDLE 401 (reset csrf)
api.interceptors.response.use(
  (res) => {
    const method = String(res?.config?.method || "").toUpperCase();
    const url = String(res?.config?.url || "");
    if (method !== "GET" && (url.startsWith("/products") || url.startsWith("/bom"))) {
      clearApiCache();
    }
    return res;
  },
  (err) => {
    if (err.response?.status === 401) {
      csrfMemoryToken = null;
    }
    return Promise.reject(err);
  }
);

export default api;
